<script setup lang="ts">
import IcLoader from '@/components/Icons/IcLoader.vue'
import IcUpload2 from '@/components/Icons/IcUpload2.vue'
import { FareItems } from '@/interfaces/entities/fareItems'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { defineEmits, ref } from 'vue'
import { useI18n } from 'vue-i18n'

withDefaults(
  defineProps<{
    showFirstInvoiceHint?: boolean
  }>(),
  { showFirstInvoiceHint: false },
)

const isLoading = ref(false)
const emit = defineEmits(['fareDataLoaded'])

const store = useFareOptimizerStore()

const { t } = useI18n()

const fileInput = ref<HTMLInputElement | null>(null)

const processPdfFile = (file: File) => {
  if (file.type !== 'application/pdf') {
    alert(t('contract_optimizer.pdf.error.file'))
    return
  }

  const reader = new FileReader()

  reader.onload = () => {
    isLoading.value = true

    const result = reader.result as string

    store
      .extractTariffItems(result)
      .then((fareData: FareItems) => {
        emit('fareDataLoaded', fareData)
      })
      .catch(() => {
        alert(t('contract_optimizer.pdf.error.extract'))
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  reader.readAsDataURL(file)
}

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files?.length) return

  const file = input.files[0]
  processPdfFile(file)
}

const onDrop = (event: DragEvent) => {
  preventDefault(event)
  const file = event.dataTransfer?.files[0]
  if (file) {
    processPdfFile(file)
  }
}

const preventDefault = (event: DragEvent) => {
  event.preventDefault()
}

const triggerFileInput = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}
</script>

<template>
  <div class="flex h-full flex-col bg-white shadow-4">
    <header class="border-b-2 border-stroke p-4">
      <h1 class="font-satoshi text-base font-bold text-black">
        {{ t('contract_optimizer.invoice.reader') }}
      </h1>
    </header>
    <div class="flex flex-1 flex-col px-8 pb-8 pt-6">
      <div
        class="flex min-h-80 flex-1 flex-col items-center justify-center gap-4 border-2 border-dashed border-sky-500 bg-slate-200"
        @drop="onDrop"
        @dragover="preventDefault">
        <input type="file" accept="application/pdf" class="hidden" @change="handleFileUpload" ref="fileInput" />
        <button class="flex content-center items-center justify-center border border-stroke bg-white p-3 outline-none" @click="triggerFileInput">
          <div v-if="isLoading" class="animate-spin items-center">
            <IcLoader />
          </div>
          <IcUpload2 v-else class="h-6 w-6" />
        </button>
        <div class="flex flex-col px-3 text-center font-satoshi text-sm font-medium text-black">
          <span>{{ t('contract_optimizer.pdf.reader') }}</span>
          <span>{{ t('contract_optimizer.pdf.not_compatible') }}</span>
          <span v-if="showFirstInvoiceHint" class="mt-2 text-xs font-normal text-slate-500">
            {{ t('contract_optimizer.pdf.first_invoice_hint') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
